import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { Spacing } from 'zens-storybook'
import { Suspense, useRef } from 'react'
import { InsightTrendId } from 'zens-store'
import {
  useNumberParams,
  useParams,
  usePeriodParam,
  useWeekdaysParam,
  useWorkingHoursParam,
  useArrayParams,
} from 'zens-router'

import {
  LayoutHeader,
  InsightsHeader,
  InsightsTrend,
  GraphBar,
  InsightsHead,
  InsightsHeadItem,
  InsightsTrendRef,
} from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
  },
  head: {
    zIndex: 100,
  },
})

interface Props extends ViewProps {}

const InsightsTrendScreen = (props: Props) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  const [{ companyId, buildingId }] = useNumberParams()
  const [{ trendId }] = useParams()
  const [period] = usePeriodParam()
  const [weekdays] = useWeekdaysParam()
  const [workingHours] = useWorkingHoursParam()
  const [{ bars }, setArrayParams] = useArrayParams<GraphBar>()
  const insights = useRef<InsightsTrendRef>(null)

  const titleForType = (type: string) => {
    switch (type) {
      case 'floor':
        return `${t('Trends')}: ${t('By floor')}`
      case 'zone':
        return `${t('Trends')}: ${t('By zone')}`
      case 'workspacetype':
        return `${t('Trends')}: ${t('By workspace type')}`
      case 'weekday':
        return `${t('Trends')}: ${t('By weekday')}`
      default:
        return ''
    }
  }

  function updateGraphBars(bars: GraphBar[]) {
    setArrayParams({ bars })
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <InsightsHeader
          companyId={companyId}
          buildingId={buildingId}
          title={titleForType(trendId)}
          breadcrumbItems={[{ title: titleForType(trendId) }]}
        />
      </Suspense>
      <View style={styles.content}>
        <InsightsHead
          companyId={companyId}
          buildingId={buildingId}
          style={styles.head}
          items={InsightsHeadItem.Building | InsightsHeadItem.Period}
          floorExtraItems={[]}
          downloadItems={[
            {
              key: 'data',
              icon: 'download',
              title: t('Download data'),
              testID: 'InsightsTrend.DownloadData',
            },
            {
              key: 'graph',
              icon: 'download',
              title: t('Download graph'),
              testID: 'InsightsTrend.DownloadGraph',
            },
          ]}
          onDownload={(key) => insights.current?.download(key)}
          href={(buildingId) => `/companies/${companyId}/insights/${buildingId}/trend/${trendId}`}
        />
        <Suspense fallback={<InsightsTrend.Skeleton trendId={trendId as InsightTrendId} />}>
          <InsightsTrend
            ref={insights}
            companyId={companyId}
            buildingId={buildingId}
            trendId={trendId as InsightTrendId}
            period={period}
            weekdays={weekdays}
            workingHours={workingHours}
            bars={bars}
            onChangeBars={updateGraphBars}
          />
        </Suspense>
      </View>
    </View>
  )
}

export default InsightsTrendScreen
